import { jsxs, jsx } from "react/jsx-runtime";
import { Box, Text, Button, Anchor } from "@zillow/constellation";
import PropTypes from "prop-types";
import "react";
import styled from "styled-components";
import withPreload from "../../utils/with-preload.esm.js";
import PostReadTime from "../PostReadTime/PostReadTime.esm.js";
import Source from "../Source/Source.esm.js";
import { getPostCardImage, getPostCardImageRadius } from "./image-util.esm.js";
const StyledAnchor = styled(Anchor).withConfig({
  displayName: "PostCard__StyledAnchor",
  componentId: "sc-1ln4qre-0"
})(["text-decoration:none !important;color:inherit !important;"]);
const SourcePreload = withPreload(Source);
const PreloadBox = withPreload(Box);
const styles = {
  vertical: {
    sm: {
      headingFontType: "h6",
      borderRadius: "$radii.md",
      containerStyles: {
        columnGap: 0,
        gridTemplateRows: "auto 1fr",
        height: "100%"
      },
      bodyStyles: {
        gridColumnEnd: "span 12",
        padding: "$spacing.md",
        justifyContent: "start",
        "@media $sm_lte": {
          padding: "$spacing.sm"
        }
      },
      imageStyles: {
        gridColumnEnd: "span 12"
      }
    },
    md: {
      containerStyles: {
        columnGap: 0,
        gridTemplateRows: "auto 1fr",
        height: "100%"
      },
      headingFontType: "h5",
      borderRadius: "$radii.md",
      bodyStyles: {
        gridColumnEnd: "span 12",
        padding: "$spacing.md",
        justifyContent: "start",
        "@media $sm_lte": {
          padding: "$spacing.sm"
        }
      },
      imageStyles: {
        gridColumnEnd: "span 12"
      }
    },
    lg: {
      containerStyles: {
        columnGap: 0,
        gridTemplateRows: "auto 1fr",
        height: "100%"
      },
      headingFontType: {
        default: "h4",
        sm_lte: "h6"
      },
      excerptFontType: "body",
      excerptColor: "textMedium",
      borderRadius: "$radii.md",
      bodyStyles: {
        gridColumnEnd: "span 12",
        padding: "$spacing.lg",
        justifyContent: "start",
        "@media $sm_lte": {
          padding: "$spacing.sm"
        }
      },
      imageStyles: {
        gridColumnEnd: "span 12"
      }
    },
    xl: {
      headingFontType: {
        default: "h3",
        lg_lte: "bodyHeading"
      },
      excerptFontType: "body",
      borderRadius: "$radii.md",
      excerptColor: "textMedium",
      containerStyles: {
        backgroundColor: "transparent",
        columnGap: 0,
        gridTemplateRows: "auto 1fr",
        height: "100%"
      },
      imageStyles: {
        gridColumnEnd: "span 12"
      },
      bodyStyles: {
        gridColumnEnd: "span 12",
        padding: "$spacing.lg",
        justifyContent: "start",
        "@media $sm_lte": {
          padding: "$spacing.sm"
        }
      }
    }
  },
  horizontal: {
    sm: {},
    md: {
      headingFontType: "h6",
      borderRadius: "$radii.md",
      containerStyles: {
        "@media $sm_lte": {
          columnGap: 0,
          gridTemplateRows: "auto 1fr",
          height: "100%"
        }
      },
      bodyStyles: {
        gridColumnEnd: "span 6",
        padding: "$spacing.md $spacing.md $spacing.md 0",
        "@media $sm_lte": {
          padding: "$spacing.sm",
          gridColumnEnd: "span 12"
        }
      },
      imageStyles: {
        gridColumnEnd: "span 6",
        "@media $sm_lte": {
          gridColumnEnd: "span 12"
        }
      }
    },
    lg: {
      headingFontType: {
        default: "h4",
        sm_lte: "h6"
      },
      excerptFontType: "body",
      excerptColor: "textMedium",
      borderRadius: "$radii.md",
      containerStyles: {
        columnGap: "$spacing.xxl",
        "@media $sm_lte": {
          columnGap: 0,
          gridTemplateRows: "auto 1fr",
          height: "100%"
        }
      },
      bodyStyles: {
        gridColumnEnd: "span 6",
        padding: "$spacing.lg $spacing.lg $spacing.lg 0",
        "@media $sm_lte": {
          padding: "$spacing.sm",
          gridColumnEnd: "span 12"
        }
      },
      imageStyles: {
        gridColumnEnd: "span 6",
        "@media $sm_lte": {
          gridColumnEnd: "span 12"
        }
      }
    },
    xl: {
      headingFontType: {
        default: "h3",
        sm_lte: "h6"
      },
      excerptFontType: "body",
      borderRadius: "$radii.md",
      excerptColor: "textMedium",
      containerStyles: {
        backgroundColor: "transparent",
        "@media $sm_lte": {
          columnGap: 0,
          gridTemplateRows: "auto 1fr",
          height: "100%"
        }
      },
      imageStyles: {
        gridColumnEnd: "span 7",
        "@media $sm_lte": {
          gridColumnEnd: "span 12"
        }
      },
      bodyStyles: {
        gridColumn: "span 5",
        padding: "$spacing.lg 0 $spacing.lg $spacing.lg",
        "@media $sm_lte": {
          padding: "$spacing.sm",
          gridColumn: "span 12"
        }
      }
    }
  }
};
const PostCard = ({
  url,
  image,
  title,
  excerpt,
  size,
  readTime,
  alignment,
  loading,
  ...rest
}) => {
  var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k, _l, _m, _n;
  const headingFontType = (_b = (_a = styles == null ? void 0 : styles[alignment]) == null ? void 0 : _a[size]) == null ? void 0 : _b.headingFontType;
  const excerptFontType = (_d = (_c = styles == null ? void 0 : styles[alignment]) == null ? void 0 : _c[size]) == null ? void 0 : _d.excerptFontType;
  const excerptColor = (_f = (_e = styles == null ? void 0 : styles[alignment]) == null ? void 0 : _e[size]) == null ? void 0 : _f.excerptColor;
  const borderRadius = ((_h = (_g = styles == null ? void 0 : styles[alignment]) == null ? void 0 : _g[size]) == null ? void 0 : _h.borderRadius) || "$radii.md";
  const bodyStyles = ((_j = (_i = styles == null ? void 0 : styles[alignment]) == null ? void 0 : _i[size]) == null ? void 0 : _j.bodyStyles) || {};
  const imageStyles = ((_l = (_k = styles == null ? void 0 : styles[alignment]) == null ? void 0 : _k[size]) == null ? void 0 : _l.imageStyles) || {};
  const containerStyles = ((_n = (_m = styles == null ? void 0 : styles[alignment]) == null ? void 0 : _m[size]) == null ? void 0 : _n.containerStyles) || {};
  const hasBoxShadow = size !== "xl";
  const cardImage = getPostCardImage({
    size,
    alignment,
    image
  });
  const cardImageRadius = getPostCardImageRadius({
    size,
    alignment
  });
  const badgeLabel = /* @__PURE__ */ jsx(Box, { sx: {
    display: "flex",
    marginBottom: "$spacing.xs"
  }, children: /* @__PURE__ */ jsx(PostReadTime, { readTime }) });
  return /* @__PURE__ */ jsxs(Box, { "data-testid": "post-card-container", ...rest, sx: {
    display: "grid",
    position: "relative",
    gridTemplateColumns: "repeat(12, 1fr)",
    borderRadius: `${borderRadius}`,
    boxShadow: hasBoxShadow ? "$shadows.shadow300" : "none",
    columnGap: "$spacing.md",
    textDecoration: "none",
    backgroundColor: "$colors.white",
    overflow: "hidden",
    ...containerStyles
  }, children: [
    url && size !== "xl" && /* @__PURE__ */ jsx(Box, { as: "a", sx: {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0
    }, ...url }),
    (cardImage == null ? void 0 : cardImage.sourceUrl) && /* @__PURE__ */ jsxs(Box, { sx: {
      ...imageStyles
    }, as: "picture", children: [
      (cardImage == null ? void 0 : cardImage.sizes) && Object.keys(cardImage == null ? void 0 : cardImage.sizes).map((breakpoint) => {
        var _a2, _b2, _c2, _d2, _e2, _f2;
        return /* @__PURE__ */ jsx(SourcePreload, { media: breakpoint, srcSet: (_b2 = (_a2 = cardImage == null ? void 0 : cardImage.sizes) == null ? void 0 : _a2[breakpoint]) == null ? void 0 : _b2.sourceUrl, width: (_d2 = (_c2 = cardImage == null ? void 0 : cardImage.sizes) == null ? void 0 : _c2[breakpoint]) == null ? void 0 : _d2.width, height: (_f2 = (_e2 = cardImage == null ? void 0 : cardImage.sizes) == null ? void 0 : _e2[breakpoint]) == null ? void 0 : _f2.height, loading, decoding: loading === "lazy" ? "async" : void 0 }, breakpoint);
      }),
      /* @__PURE__ */ jsx(PreloadBox, { sx: {
        aspectRatio: "16 / 9",
        width: "100%",
        height: "100%",
        // cardImageRadius can be a single value or an object with individual corners
        ...typeof cardImageRadius === "object" ? cardImageRadius : {
          borderRadius: cardImageRadius
        },
        objectFit: "cover"
      }, as: "img", src: cardImage.sourceUrl, alt: cardImage.alt || title, width: cardImage.width, height: cardImage.height, loading, decoding: loading === "lazy" ? "async" : void 0 })
    ] }),
    /* @__PURE__ */ jsxs(Box, { sx: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "flex-start",
      ...bodyStyles
    }, children: [
      badgeLabel,
      size !== "xl" ? /* @__PURE__ */ jsx(Text, { fontType: headingFontType, as: "h2", dangerouslySetInnerHTML: {
        __html: title
      } }) : /* @__PURE__ */ jsx(Text, { fontType: headingFontType, as: "h2", children: /* @__PURE__ */ jsx(StyledAnchor, { ...url, dangerouslySetInnerHTML: {
        __html: title
      } }) }),
      excerpt && /* @__PURE__ */ jsx(Text, { fontType: excerptFontType, as: "p", marginTop: "sm", fontColor: excerptColor, dangerouslySetInnerHTML: {
        __html: excerpt
      } }),
      size === "xl" && url && /* @__PURE__ */ jsx(Button, { size: "md", as: "a", buttonType: "primary", marginTop: "md", ...url, children: "Read article" })
    ] })
  ] });
};
const SizePropType = PropTypes.shape({
  sourceUrl: PropTypes.string.isRequired,
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired
});
PostCard.propTypes = {
  alignment: PropTypes.oneOf(["vertical", "horizontal"]),
  image: PropTypes.shape({
    sourceUrl: PropTypes.string.isRequired,
    altText: PropTypes.string.isRequired,
    mediaDetails: PropTypes.shape({
      width: PropTypes.number,
      height: PropTypes.number,
      sizes: PropTypes.shape({
        small: SizePropType,
        medium: SizePropType,
        large: SizePropType,
        full: SizePropType
      })
    })
  }),
  category: PropTypes.shape({
    name: PropTypes.string.isRequired,
    link: PropTypes.string.isRequired
  }),
  readTime: PropTypes.number,
  url: PropTypes.shape({
    href: PropTypes.string.isRequired
  }),
  title: PropTypes.string.isRequired,
  excerpt: PropTypes.string,
  size: PropTypes.oneOf(["sm", "md", "lg", "xl"]),
  children: PropTypes.node,
  loading: PropTypes.string,
  forceShowCategory: PropTypes.bool,
  forceHideReadTime: PropTypes.bool
};
PostCard.defaultProps = {
  alignment: "vertical",
  loading: "lazy",
  size: "lg",
  excerpt: null,
  readTime: 5,
  image: {
    sourceUrl: "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAlgAAAHCBAMAAADBXapXAAAAJ1BMVEX29vrR0dXa2t7T09f09Pju7vLW1trk5Ojx8fXh4eXp6e3l5enc3OB7H3z4AAAE2ElEQVR42uzasUocURTH4VMo7sbqMLBBYrOVabVLKklnt5Bgv0UgpEreIKTYOkLsA75AhDxgVld3Vta594ql3/cOl/n/DhMAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAwDONfweNRtPJj6DNeebXoMn+cWZ+C1pc5NLhaVC3yFtfgqrded7q/gQ113nndVBxlmtvg6LRPNeMrYqr3PAmKNjLnrFVtjPNJQ+xfWJt+hwMeHXTOR5ia+dsOQge9TMfUD2Vztk2mQUDndNTPcXOGeDEXOwcY6viYw56Hzywnz1jq2znIgsOPcSBzjG26p1T1P0N7n3PNWOr4kMueYhP7hxjq+IqBzgxbxlnk19BjKa55CE2Oc8BTsy1zlE95c5pdvDSf6xZZFl3dGxs3dmtdM7RbPN2M3nZP9ZcZ8nJv5tuVD0rZ1nQXZ6uwtGJuT8lD3g3i5WxsVXpnO6y/2DOnZgj9nLQyafoLYyt/+zcsW/TQBTH8TfYagnTc9REbRgwFapEJw+IRuqQCaTAECoBqrpQCSS6EVr2wJABlqCKDoghQQywVQgkxFbxj+E0je+efXexYWC432fu9JV7vty9RI7MSJsd+crEEfNDtmgckTTx/mTrcuJY2aUl74+Y37HZ9Y7hbz0/Yn7t/heUbnt+xLzPJtu9EhuyBvnG9hY0u8868o15I2rzCrEcy5VUSxBLiHquS1jE0t3qkNU9RizdjY7rcAKxdO/dc26IpdSPyKHGiKVE38mlj1jiNehyiRGL51bdrYIYsUhsGRyGjFhUstXyPmIROVoFD/QHC7GI7K3Ck7F2/I5YKWurO8lTfT+KWClLq+CTfpFaY8SaMrcKD8UV/QSxzhmnrboxc9M46Ob3sXI0MC1X8sq5zwbbv/ybaetRwQ6nWu7r/YMvBETBIU+NXQ/WwWeCVHicn5JZQiqLMOapes/6YLV/EqilPXXTduYXvSSY2U248GBNWKl/8O8NaLPDF1bMD9YGfghD+4QzNzZ9KmxjXZdbhpk1yiwn6k4fsiqqFZ9SZoj/wKLwjDPNwgFphO2C3F4pz/PTa5t4B9paNUk5m10nglKLWTOSn3Q28FiJVtbhxz7XsWO3tpJjtSG38RK0t5JfYRpiZS+s7cIWKT8IhJglv7+kugB+maeCv/8ZgjcfyTcsRFRSsPfbx3tD4VnJVLuxn5esuvqgZCpfb6R1a1TC3rq/1/cVl/fusc+zDportEj4wu/BkArLe/AVX3TKDMjpbez9yJHSIpfwBPNZrIyc24UEw2xU7vdluuuY/BOxVsgm+IYxyXMlNll3Y0YsGavheKwQKx+rY3usEGuOM1eNwyIJI5Yh1qp5ZhKxFFZOKedRwohlibVSXNkRS7Ae/YUxI1YOa57IUVzEcsWKxHgpYhWwbqRGJhFrUaxWtlwhlgkLvYsv0CFWiVhbs6UdscrEagymrRDLgqXHaSvEKhmrnjBiWXEeYiEWYi2AWBUgVgWIVQFiVYBYFSBWBYhVAWJVgFgVINb/dO0fEAAAAAAAAAD8YQ8OBAAAAACA/F8bQVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVaQ8OSAAAAAAE/X/dj1ABAAAAAAAAAAAAAACAlQB0Y5H7ngzE2AAAAABJRU5ErkJggg==",
    altText: "",
    mediaDetails: {
      sizes: {
        full: {
          width: 720,
          height: 405,
          sourceUrl: "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAlgAAAHCBAMAAADBXapXAAAAJ1BMVEX29vrR0dXa2t7T09f09Pju7vLW1trk5Ojx8fXh4eXp6e3l5enc3OB7H3z4AAAE2ElEQVR42uzasUocURTH4VMo7sbqMLBBYrOVabVLKklnt5Bgv0UgpEreIKTYOkLsA75AhDxgVld3Vta594ql3/cOl/n/DhMAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAwDONfweNRtPJj6DNeebXoMn+cWZ+C1pc5NLhaVC3yFtfgqrded7q/gQ113nndVBxlmtvg6LRPNeMrYqr3PAmKNjLnrFVtjPNJQ+xfWJt+hwMeHXTOR5ia+dsOQge9TMfUD2Vztk2mQUDndNTPcXOGeDEXOwcY6viYw56Hzywnz1jq2znIgsOPcSBzjG26p1T1P0N7n3PNWOr4kMueYhP7hxjq+IqBzgxbxlnk19BjKa55CE2Oc8BTsy1zlE95c5pdvDSf6xZZFl3dGxs3dmtdM7RbPN2M3nZP9ZcZ8nJv5tuVD0rZ1nQXZ6uwtGJuT8lD3g3i5WxsVXpnO6y/2DOnZgj9nLQyafoLYyt/+zcsW/TQBTH8TfYagnTc9REbRgwFapEJw+IRuqQCaTAECoBqrpQCSS6EVr2wJABlqCKDoghQQywVQgkxFbxj+E0je+efXexYWC432fu9JV7vty9RI7MSJsd+crEEfNDtmgckTTx/mTrcuJY2aUl74+Y37HZ9Y7hbz0/Yn7t/heUbnt+xLzPJtu9EhuyBvnG9hY0u8868o15I2rzCrEcy5VUSxBLiHquS1jE0t3qkNU9RizdjY7rcAKxdO/dc26IpdSPyKHGiKVE38mlj1jiNehyiRGL51bdrYIYsUhsGRyGjFhUstXyPmIROVoFD/QHC7GI7K3Ck7F2/I5YKWurO8lTfT+KWClLq+CTfpFaY8SaMrcKD8UV/QSxzhmnrboxc9M46Ob3sXI0MC1X8sq5zwbbv/ybaetRwQ6nWu7r/YMvBETBIU+NXQ/WwWeCVHicn5JZQiqLMOapes/6YLV/EqilPXXTduYXvSSY2U248GBNWKl/8O8NaLPDF1bMD9YGfghD+4QzNzZ9KmxjXZdbhpk1yiwn6k4fsiqqFZ9SZoj/wKLwjDPNwgFphO2C3F4pz/PTa5t4B9paNUk5m10nglKLWTOSn3Q28FiJVtbhxz7XsWO3tpJjtSG38RK0t5JfYRpiZS+s7cIWKT8IhJglv7+kugB+maeCv/8ZgjcfyTcsRFRSsPfbx3tD4VnJVLuxn5esuvqgZCpfb6R1a1TC3rq/1/cVl/fusc+zDportEj4wu/BkArLe/AVX3TKDMjpbez9yJHSIpfwBPNZrIyc24UEw2xU7vdluuuY/BOxVsgm+IYxyXMlNll3Y0YsGavheKwQKx+rY3usEGuOM1eNwyIJI5Yh1qp5ZhKxFFZOKedRwohlibVSXNkRS7Ae/YUxI1YOa57IUVzEcsWKxHgpYhWwbqRGJhFrUaxWtlwhlgkLvYsv0CFWiVhbs6UdscrEagymrRDLgqXHaSvEKhmrnjBiWXEeYiEWYi2AWBUgVgWIVQFiVYBYFSBWBYhVAWJVgFgVINb/dO0fEAAAAAAAAAD8YQ8OBAAAAACA/F8bQVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVaQ8OSAAAAAAE/X/dj1ABAAAAAAAAAAAAAACAlQB0Y5H7ngzE2AAAAABJRU5ErkJggg=="
        }
      }
    }
  },
  url: null
};
export {
  PostCard as default
};
